import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Statistic,
  Row,
  Col,
  Progress,
  Timeline,
  Badge,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagsOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { activityApis } from "../../../api/Activity";
import { normsApis } from "../../../api/Norms";
import NormMaterialRollupCard from "../norms/NormMaterialRollupCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getColorByLabel } from "../projects/ProjectInformationById";
import { useWbsName } from "../shared/useWbsCodes";

interface ActivityQuickViewCardProps {
  activityId?: number;
  derivedProjectStatus?: string;
  onClose?: () => void;
  onRefresh?: () => void;
}

const ActivityQuickViewCard: FC<ActivityQuickViewCardProps> = ({
  activityId: propActivityId,
  derivedProjectStatus,
  onClose,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<any>(null);
  const wbsName = useWbsName();

  const { data: ActivityInDetailModal } = useSelector(
    (state: RootState) => state.ActivityInDetailModal
  );
  const activityId = propActivityId || ActivityInDetailModal?.id;

  const fetchActivityData = async () => {
    if (!activityId) return;
    setLoading(true);
    try {
      const response = await activityApis.fetchActivityById(
        activityId,
        derivedProjectStatus
      );
      if (response?.status && response?.data) {
        setActivityData(response.data);
      }
    } catch (error) {
      console.error("Error fetching activity data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityData();
  }, [activityId]);

  // Rolled-up norm material requirement across this activity's tasks.
  const [normMaterials, setNormMaterials] = useState<any[]>([]);
  const [normMatLoading, setNormMatLoading] = useState(false);
  useEffect(() => {
    if (!activityId) return;
    let active = true;
    (async () => {
      try {
        setNormMatLoading(true);
        const res = await normsApis.getActivityNormMaterials(Number(activityId));
        if (active && res?.status === true)
          setNormMaterials(
            Array.isArray(res?.data?.materials) ? res.data.materials : [],
          );
      } catch {
        /* non-fatal */
      } finally {
        if (active) setNormMatLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [activityId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!activityData) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
        No activity data available
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active" || statusLower === "inprogress") return "processing";
    if (statusLower === "completed" || statusLower === "closed") return "success";
    if (statusLower === "onhold") return "warning";
    if (statusLower === "cancelled" || statusLower === "deleted") return "error";
    if (statusLower === "draft") return "default";
    return "default";
  };

  const completionPercent = parseFloat(activityData.tasksCompletionPercentage || "0");

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h4
            style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#1a1a2e" }}
            title={activityData.activityName}
          >
            {wbsName(activityData.activityName)}
          </h4>
          <Badge status={getStatusColor(activityData.status)} text={
            <span style={{ fontWeight: 500, fontSize: 14 }}>{activityData.status}</span>
          } />
        </div>
        {activityData.flag && (
          <Tag color={activityData.flag === "Internal" ? "blue" : "orange"} style={{ marginTop: 4 }}>
            <FlagOutlined /> {activityData.flag}
          </Tag>
        )}
      </div>

      {/* Progress Section */}
      <Card
        size="small"
        style={{
          marginBottom: 20,
          borderRadius: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
        styles={{ body: { padding: "16px 20px" } }}
      >
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Statistic
              title={<span style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>Tasks Completed</span>}
              value={activityData.completedTasks || 0}
              suffix={<span style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>/ {activityData.totalTasks || 0}</span>}
              valueStyle={{ color: "#fff", fontSize: 28, fontWeight: 600 }}
            />
          </Col>
          <Col span={12}>
            <div style={{ textAlign: "right" }}>
              <Progress
                type="circle"
                percent={completionPercent}
                size={70}
                strokeColor="#52c41a"
                trailColor="rgba(255,255,255,0.3)"
                format={(percent) => (
                  <span style={{ color: "#fff", fontWeight: 600 }}>{percent?.toFixed(0)}%</span>
                )}
              />
            </div>
          </Col>
        </Row>
        {/* Budget progress: consumed vs planned 3M value of the activity's tasks */}
        {(() => {
          const consumed = Number(activityData.budgetConsumption) || 0;
          const planned = Number(activityData.budgetPlanned) || 0;
          const fmt = (n: number) =>
            `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
          const percent =
            planned > 0 ? Math.round((consumed / planned) * 100) : 0;
          return (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}
                >
                  Budget Progress (3M Consumption)
                </span>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                  {fmt(consumed)}
                  {planned > 0 && (
                    <span
                      style={{ color: "rgba(255,255,255,0.7)", fontWeight: 400 }}
                    >
                      {" "}
                      / {fmt(planned)} ({percent}%)
                    </span>
                  )}
                </span>
              </div>
              <Progress
                percent={
                  planned > 0
                    ? Math.min(100, percent)
                    : consumed > 0
                    ? 100
                    : 0
                }
                showInfo={false}
                strokeLinecap="butt"
                size={["100%", 8]}
                strokeColor={
                  (planned > 0 && percent > 100) ||
                  (planned <= 0 && consumed > 0)
                    ? "#ff4d4f"
                    : "#52c41a"
                }
                trailColor="rgba(255,255,255,0.3)"
              />
            </div>
          );
        })()}
      </Card>

      {/* Quick Stats */}
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, background: "#f0f9ff", border: "1px solid #bae0ff" }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: "#0958d9" }}>Total Tasks</span>}
              value={activityData.totalTasks || 0}
              valueStyle={{ color: "#0958d9", fontSize: 22 }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" style={{ borderRadius: 10, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: "#389e0d" }}>Completed Tasks</span>}
              value={activityData.completedTasks || 0}
              valueStyle={{ color: "#389e0d", fontSize: 22 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Details Section */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Activity Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        styles={{ header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 }, body: { padding: "12px 16px" } }}
      >
        <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}>
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 6 }} />Owner</>}>
            {activityData.activityOwnerName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><FlagOutlined style={{ marginRight: 6 }} />Flag</>}>
            {activityData.flag || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Planned Start Date</>}>
            {activityData.activityStartDate ? dayjs(activityData.activityStartDate).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Planned End Date</>}>
            {activityData.activityEndDate ? dayjs(activityData.activityEndDate).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Actual Start</>}>
            {activityData.actualStartDate ? dayjs(activityData.actualStartDate).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Actual End</>}>
            {activityData.actualEndDate ? dayjs(activityData.actualEndDate).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><FileTextOutlined style={{ marginRight: 6 }} />Budget Line</>}>
            {activityData.activityBudgetLineName || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><PercentageOutlined style={{ marginRight: 6 }} />Weightage</>}>
            {activityData.activityWeightage ? `${activityData.activityWeightage}%` : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label={<><ClockCircleOutlined style={{ marginRight: 6 }} />Created</>}>
            {activityData.createdAt ? dayjs(activityData.createdAt).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Phase & Project Info */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Hierarchy
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        styles={{ header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 }, body: { padding: "12px 16px" } }}
      >
        <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}>
          <Descriptions.Item label="Project">
            {activityData.projectTitle || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Phase">
            {wbsName(activityData.phaseName) || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Tags Section */}
      {activityData.activityTags && activityData.activityTags.length > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              <TagsOutlined style={{ marginRight: 8 }} />
              Tags
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{ header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 }, body: { padding: "12px 16px" } }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {activityData.activityTags.map((tag: any, index: number) => {
              const color = getColorByLabel(tag.tagName || "");
              return (
                <Tag
                  key={index}
                  style={{
                    backgroundColor: color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {tag.tagName}
                </Tag>
              );
            })}
          </div>
        </Card>
      )}

      {/* Resources Allocated */}
      {activityData.activityResources && activityData.activityResources.length > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
              Manpower Allocated
            </span>
          }
          style={{ borderRadius: 12, marginBottom: 20 }}
          styles={{ header: { borderBottom: "1px solid #f0f0f0", minHeight: 40 }, body: { padding: "12px 16px" } }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {activityData.activityResources.map((resource: any, index: number) => (
              <Tag key={index} color="geekblue">
                {resource.laborTypeName} - {resource.laborCategoryName}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* Materials from Norm — rolled up across this activity's tasks */}
      <NormMaterialRollupCard
        materials={normMaterials}
        loading={normMatLoading}
      />

      {/* On Hold Info */}
      {activityData.status?.toLowerCase() === "onhold" && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              On Hold Information
            </span>
          }
          style={{ borderRadius: 12, marginTop: 20, background: "#fffbe6", border: "1px solid #ffe58f" }}
          styles={{ header: { borderBottom: "1px solid #ffe58f", minHeight: 40, background: "#fffbe6" }, body: { padding: "12px 16px" } }}
        >
          <Timeline
            items={[
              {
                color: "orange",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Hold Date</div>
                    <div style={{ color: "#666" }}>
                      {activityData.activityOnholdDate ? dayjs(activityData.activityOnholdDate).format("DD MMM YYYY") : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "green",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Expected Resolution</div>
                    <div style={{ color: "#666" }}>
                      {activityData.activityETAResolution ? dayjs(activityData.activityETAResolution).format("DD MMM YYYY") : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "blue",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Reason</div>
                    <div style={{ color: "#666" }}>{activityData.activityOnholdReason || "No reason provided"}</div>
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* Owner Reassignment Info */}
      {activityData.originalActivityOwnerId && activityData.originalActivityOwnerId !== activityData.activityOwnerId && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1890ff" }}>
              Owner Reassignment
            </span>
          }
          style={{ borderRadius: 12, marginTop: 20, background: "#e6f7ff", border: "1px solid #91d5ff" }}
          styles={{ header: { borderBottom: "1px solid #91d5ff", minHeight: 40, background: "#e6f7ff" }, body: { padding: "12px 16px" } }}
        >
          <Descriptions column={1} size="small" labelStyle={{ fontWeight: 500, color: "#666" }}>
            <Descriptions.Item label="Original Owner">
              {activityData.originalActivityOwnerName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
              {activityData.reassignedReason || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};

export default ActivityQuickViewCard;
